// File: frontend/src/pages/SuperAdmin/components/SystemSettings/GeographyUpload/index.js

import React, { useState, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUpload, 
  faFileAlt, 
  faCheck, 
  faTimes, 
  faSpinner,
  faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { geoService } from '../../../../../services/superAdmin/geoService';
import styles from './GeographyUpload.module.css';

const GeographyUpload = ({ onUploadComplete }) => {
  const { t } = useLanguage();
  const fileInputRef = useRef(null);
  
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [geoType, setGeoType] = useState('');
  const [parentGeoId, setParentGeoId] = useState('NONE');
  const [parentOptions, setParentOptions] = useState([{ geo_id: 'NONE', geo_name: t('no_parent') }]);
  const [uploadStatus, setUploadStatus] = useState('idle'); // idle, uploading, success, error
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResults, setUploadResults] = useState([]);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [idPrefix, setIdPrefix] = useState('');
  const [codePrefix, setCodePrefix] = useState('');
  
  // Predefined geography types
  const geoTypes = [
    { value: 'country', label: t('geo_type_country') },
    { value: 'province', label: t('geo_type_province') },
    { value: 'state', label: t('geo_type_state') },
    { value: 'county', label: t('geo_type_county') },
    { value: 'census_division', label: t('geo_type_census_division') },
    { value: 'census_subdivision', label: t('geo_type_census_subdivision') },
    { value: 'electoral_district', label: t('geo_type_electoral_district') },
    { value: 'fsa', label: t('geo_type_fsa') },
    { value: 'custom', label: t('geo_type_custom') }
  ];
  
  // Fetch parent geographies for dropdown
  const fetchParentOptions = async () => {
    try {
      // Get all geographies for parent selection
      const response = await geoService.getGeographies();
      
      // Extract geographies from the response
      const geographiesData = response.geography_entries || 
                            (response.data && response.data.geography_entries) || 
                            [];
      
      setParentOptions([
        { geo_id: 'NONE', geo_name: t('no_parent') },
        ...geographiesData
      ]);
    } catch (err) {
      console.error('Error fetching parent options:', err);
    }
  };
  
  // Handle file selection
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    
    // Filter for GeoJSON and JSON files
    const validFiles = files.filter(file => 
      file.name.endsWith('.json') || 
      file.name.endsWith('.geojson')
    );
    
    setSelectedFiles(validFiles);
    
    // Fetch parent options if not already loaded
    if (parentOptions.length <= 1) {
      fetchParentOptions();
    }
  };
  
  // Trigger file input click
  const handleBrowseClick = () => {
    fileInputRef.current.click();
  };
  
  // Process and upload files
  const handleUpload = async () => {
    if (selectedFiles.length === 0 || !geoType) {
      return;
    }
    
    setUploadStatus('uploading');
    setUploadProgress(0);
    setUploadResults([]);
    
    const results = [];
    let successCount = 0;
    
    for (let i = 0; i < selectedFiles.length; i++) {
      const file = selectedFiles[i];
      const progress = Math.round(((i + 1) / selectedFiles.length) * 100);
      setUploadProgress(progress);
      
      try {
        // Read file content
        const fileContent = await readFileAsText(file);
        
        // Parse GeoJSON
        const geoJson = JSON.parse(fileContent);
        
        // Process file based on its structure
        if (geoJson.type === 'FeatureCollection') {
          // Process each feature in the collection
          for (const feature of geoJson.features) {
            const result = await processFeature(feature, file.name, geoType, parentGeoId);
            results.push(result);
            if (result.status === 'success') successCount++;
          }
        } else if (geoJson.type === 'Feature') {
          // Process single feature
          const result = await processFeature(geoJson, file.name, geoType, parentGeoId);
          results.push(result);
          if (result.status === 'success') successCount++;
        } else {
          // Handle other GeoJSON types
          results.push({
            fileName: file.name,
            status: 'error',
            message: `Unsupported GeoJSON type: ${geoJson.type}`
          });
        }
      } catch (error) {
        results.push({
          fileName: file.name,
          status: 'error',
          message: error.message
        });
      }
    }
    
    setUploadResults(results);
    setUploadStatus(successCount > 0 ? 'success' : 'error');
    
    // Notify parent component
    if (onUploadComplete) {
      onUploadComplete(results);
    }
  };
  
  // Read file as text
  const readFileAsText = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = (e) => reject(new Error('Failed to read file'));
      reader.readAsText(file);
    });
  };
  
  // Process a GeoJSON feature
  const processFeature = async (feature, fileName, geoType, parentGeoId) => {
    try {
      // Extract properties
      const properties = feature.properties || {};
      
      // Generate ID and code
      let geoId = properties.id || properties.ID || properties.geo_id || properties.GEO_ID;
      let geoCode = properties.code || properties.CODE || properties.geo_code || properties.GEO_CODE;
      let geoName = properties.name || properties.NAME || properties.geo_name || properties.GEO_NAME;
      
      // If no ID or code, generate from filename
      if (!geoId) {
        const baseName = fileName.split('.')[0];
        geoId = `${idPrefix}${baseName}`.toLowerCase();
      }
      
      if (!geoCode) {
        geoCode = `${codePrefix}${geoId}`.toUpperCase();
      }
      
      if (!geoName) {
        geoName = geoId.replace(/_/g, ' ').replace(/-/g, ' ');
        // Capitalize first letter of each word
        geoName = geoName.split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
      }
      
      // Create geography data
      const geoData = {
        geo_id: geoId,
        geo_type: geoType,
        geo_name: geoName,
        geo_code: geoCode,
        parent_geo_id: parentGeoId,
        properties: {
          ...properties,
          source_file: fileName
        },
        geometry_boundary: feature
      };
      
      // Upload to API
      const response = await geoService.createGeography(geoData);
      
      return {
        fileName,
        geoId,
        geoName,
        status: 'success',
        message: 'Successfully uploaded'
      };
    } catch (error) {
      return {
        fileName,
        status: 'error',
        message: error.message
      };
    }
  };
  
  // Reset the form
  const handleReset = () => {
    setSelectedFiles([]);
    setGeoType('');
    setParentGeoId('NONE');
    setUploadStatus('idle');
    setUploadProgress(0);
    setUploadResults([]);
    setIdPrefix('');
    setCodePrefix('');
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  return (
    <div className={styles.uploadContainer}>
      <div className={styles.uploadHeader}>
        <h3 className={styles.uploadTitle}>{t('upload_geography_files')}</h3>
      </div>
      
      <div className={styles.uploadContent}>
        <div className={styles.uploadSection}>
          <h4 className={styles.sectionTitle}>{t('select_files')}</h4>
          
          <div className={styles.fileDropArea} onClick={handleBrowseClick}>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              multiple
              accept=".json,.geojson"
              className={styles.fileInput}
            />
            
            {selectedFiles.length === 0 ? (
              <div className={styles.dropMessage}>
                <FontAwesomeIcon icon={faUpload} size="2x" />
                <p>{t('drop_files_here')}</p>
                <button className={styles.browseButton}>
                  {t('browse_files')}
                </button>
              </div>
            ) : (
              <div className={styles.selectedFiles}>
                <div className={styles.fileListHeader}>
                  <span>{t('selected_files')}: {selectedFiles.length}</span>
                  <button 
                    className={styles.clearButton}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedFiles([]);
                      if (fileInputRef.current) {
                        fileInputRef.current.value = '';
                      }
                    }}
                  >
                    {t('clear')}
                  </button>
                </div>
                <div className={styles.fileList}>
                  {selectedFiles.map((file, index) => (
                    <div key={index} className={styles.fileItem}>
                      <FontAwesomeIcon icon={faFileAlt} />
                      <span className={styles.fileName}>{file.name}</span>
                      <span className={styles.fileSize}>
                        {(file.size / 1024).toFixed(1)} KB
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        
        <div className={styles.uploadSection}>
          <h4 className={styles.sectionTitle}>{t('upload_settings')}</h4>
          
          <div className={styles.formGroup}>
            <label htmlFor="geoType" className={styles.formLabel}>
              {t('geography_type')} <span className={styles.required}>*</span>
            </label>
            <select
              id="geoType"
              className={styles.formSelect}
              value={geoType}
              onChange={(e) => setGeoType(e.target.value)}
              required
            >
              <option value="">{t('select_geo_type')}</option>
              {geoTypes.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>
          
          <div className={styles.formGroup}>
            <label htmlFor="parentGeoId" className={styles.formLabel}>
              {t('parent_geography')}
            </label>
            <select
              id="parentGeoId"
              className={styles.formSelect}
              value={parentGeoId}
              onChange={(e) => setParentGeoId(e.target.value)}
            >
              {parentOptions.map(option => (
                <option key={option.geo_id} value={option.geo_id}>
                  {option.geo_name}
                </option>
              ))}
            </select>
          </div>
          
          <div className={styles.advancedToggle}>
            <button 
              className={styles.toggleButton}
              onClick={() => setShowAdvanced(!showAdvanced)}
              type="button"
            >
              {showAdvanced ? t('hide_advanced_options') : t('show_advanced_options')}
            </button>
          </div>
          
          {showAdvanced && (
            <div className={styles.advancedOptions}>
              <div className={styles.formGroup}>
                <label htmlFor="idPrefix" className={styles.formLabel}>
                  {t('id_prefix')}
                </label>
                <input
                  type="text"
                  id="idPrefix"
                  className={styles.formInput}
                  value={idPrefix}
                  onChange={(e) => setIdPrefix(e.target.value)}
                  placeholder="e.g., country_"
                />
                <div className={styles.formHelp}>{t('id_prefix_help')}</div>
              </div>
              
              <div className={styles.formGroup}>
                <label htmlFor="codePrefix" className={styles.formLabel}>
                  {t('code_prefix')}
                </label>
                <input
                  type="text"
                  id="codePrefix"
                  className={styles.formInput}
                  value={codePrefix}
                  onChange={(e) => setCodePrefix(e.target.value)}
                  placeholder="e.g., CTY-"
                />
                <div className={styles.formHelp}>{t('code_prefix_help')}</div>
              </div>
            </div>
          )}
        </div>
        
        <div className={styles.uploadActions}>
          <button
            className={styles.resetButton}
            onClick={handleReset}
            disabled={uploadStatus === 'uploading'}
          >
            {t('reset')}
          </button>
          <button
            className={styles.uploadButton}
            onClick={handleUpload}
            disabled={selectedFiles.length === 0 || !geoType || uploadStatus === 'uploading'}
          >
            {uploadStatus === 'uploading' ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin />
                <span>{t('uploading')}</span>
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faUpload} />
                <span>{t('upload')}</span>
              </>
            )}
          </button>
        </div>
        
        {uploadStatus === 'uploading' && (
          <div className={styles.progressContainer}>
            <div className={styles.progressBar}>
              <div 
                className={styles.progressFill} 
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
            <div className={styles.progressText}>
              {uploadProgress}% {t('complete')}
            </div>
          </div>
        )}
        
        {uploadResults.length > 0 && (
          <div className={styles.resultsContainer}>
            <h4 className={styles.resultsTitle}>
              {t('upload_results')}
              <span className={styles.resultsSummary}>
                {uploadResults.filter(r => r.status === 'success').length} {t('successful')},
                {uploadResults.filter(r => r.status === 'error').length} {t('failed')}
              </span>
            </h4>
            <div className={styles.resultsList}>
              {uploadResults.map((result, index) => (
                <div 
                  key={index} 
                  className={`${styles.resultItem} ${styles[result.status]}`}
                >
                  <div className={styles.resultIcon}>
                    <FontAwesomeIcon 
                      icon={result.status === 'success' ? faCheck : faTimes} 
                    />
                  </div>
                  <div className={styles.resultInfo}>
                    <div className={styles.resultFileName}>{result.fileName}</div>
                    {result.geoId && (
                      <div className={styles.resultGeoId}>{result.geoId}</div>
                    )}
                    <div className={styles.resultMessage}>{result.message}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      
      <div className={styles.uploadFooter}>
        <div className={styles.uploadTip}>
          <FontAwesomeIcon icon={faInfoCircle} />
          <span>{t('upload_tip')}</span>
        </div>
      </div>
    </div>
  );
};

export default GeographyUpload;